import { addClass } from '@opten/dom/add-class';
import { newGuid } from '@opten/common/new-guid';
import { createNode } from '@opten/dom/create-node';
import { isAncestorOfClass } from '@opten/dom/is-ancestor-of-class';
import { removeClass } from '@opten/dom/remove-class';
import { addTransitionEndListener } from '@opten/transition/add-transition-end-listener';

import { OverlayOptions } from './overlay-options';
import { OverlayKeyboardDispatcher } from './keyboard/overlay-keyboard-dispatcher';

// todo: do it more like angular with backdrop configuration etc...
export class Overlay {
	overlayElement: HTMLElement;
	onAttached: () => void;
	onDetached: () => void;
	beforeOpen: () => void;
	beforeClose: () => void;
	afterOpened: () => void;
	afterClosed: () => void;
	onKeyboardEvent: (event: KeyboardEvent) => void;
	backdropClick: (event: MouseEvent) => void;

	private uuid: string;
	private options: OverlayOptions;
	private overlayContainerElement: HTMLElement;
	private backdropElement: HTMLElement;
	private attached = false;
	private keyboardDispatcher = new OverlayKeyboardDispatcher(document);

	constructor(public portalElement: HTMLElement, options?: OverlayOptions) {
		this.options = new OverlayOptions(options);

		this.uuid = newGuid();

		if (options.scrollStrategy) {
			options.scrollStrategy.attach(this);
		}
	}

	create() {
		// we want to re-use existing containers
		// so search it in the dom before we create it again!
		const overlayContainerElement = document.getElementsByClassName(
			`${this.options.className}__container`
		)[0] as HTMLElement;

		if (overlayContainerElement) {
			this.overlayContainerElement = overlayContainerElement;
		} else {
			this.overlayContainerElement = createNode(
				`<div class="${this.options.className}__container"></div>`
			);

			document.body.insertBefore(
				this.overlayContainerElement,
				document.body.lastElementChild
			);
		}

		this.overlayElement = createNode(
			`<div id="c-overlay-${this.uuid}" class="${this.options.className}"></div>`
		);

		if (this.options.overlayClassName) {
			addClass(this.overlayElement, this.options.overlayClassName);
		}

		this.overlayContainerElement.appendChild(this.overlayElement);
	}

	hasAttached() {
		return this.attached;
	}

	detachBackdrop() {
		const backdropToDetach = this.backdropElement;

		if (backdropToDetach) {
			const finishDetach = () => {
				// It may not be attached to anything in certain cases (e.g. unit tests).
				if (backdropToDetach && backdropToDetach.parentNode) {
					backdropToDetach.parentNode.removeChild(backdropToDetach);
				}

				// It is possible that a new portal has been attached to this overlay since we started
				// removing the backdrop. If that is the case, only clear the backdrop reference if it
				// is still the same instance that we started to remove.
				if (this.backdropElement === backdropToDetach) {
					this.backdropElement = null;
				}
			};

			removeClass(backdropToDetach, 'is-showing');

			if (this.options.backdropClassName) {
				removeClass(backdropToDetach, this.options.backdropClassName);
			}

			addTransitionEndListener(backdropToDetach, finishDetach, 500);

			// If the backdrop doesn't have a transition, the `transitionend` event won't fire.
			// In this case we make it unclickable and we try to remove it after a delay.
			backdropToDetach.style.pointerEvents = 'none';
		}
	}

	/** Updates the position of the overlay based on the position strategy. */
	updatePosition() {
		if (this.options.positionStrategy) {
			this.options.positionStrategy.apply();
		}
	}

	getOptions() {
		return this.options;
	}

	/** Update the size properties of the overlay. */
	updateSize(sizeConfig: OverlaySizeConfig) {
		this.options = { ...this.options, ...sizeConfig };
		this._updateElementSize();
	}

	open() {
		this._attach();

		// Update the position once, so that the overlay will be fully rendered
		// before attempting to position it, as the position may depend on the size of the rendered
		// content.
		this.updatePosition();

		// animate
		addClass(this.portalElement, 'is-showing');

		if (this.beforeOpen) {
			this.beforeOpen();
		}

		// todo: Aniamte like Angular Material?
		addTransitionEndListener(
			this.portalElement,
			() => {
				removeClass(this.portalElement, 'is-showing');
				addClass(this.portalElement, 'is-visible');

				if (this.afterOpened) {
					this.afterOpened();
				}
			},
			this.options.transitionDuration
		);
	}

	close() {
		this._detach();

		// animate
		addClass(this.portalElement, 'is-fade-out');

		if (this.beforeClose) {
			this.beforeClose();
		}

		addTransitionEndListener(
			this.portalElement,
			() => {
				removeClass(this.portalElement, 'is-fade-out');
				// todo: why?! otherwise animation only works once...
				removeClass(this.portalElement, 'is-showing');
				removeClass(this.portalElement, 'is-visible');

				// should be called by caller
				// this._dispose();

				if (this.afterClosed) {
					this.afterClosed();
				}
			},
			this.options.transitionDuration
		);
	}

	/** Cleans up the overlay from the DOM. */
	dispose() {
		if (this.portalElement && this.portalElement.parentElement) {
			this.portalElement.parentElement!.removeChild(this.portalElement);
		}

		if (
			this.options.positionStrategy &&
			this.options.positionStrategy.dispose
		) {
			this.options.positionStrategy.dispose();
		}

		if (this.options.scrollStrategy) {
			this.options.scrollStrategy.disable();
		}

		this.detachBackdrop();

		// Remove this overlay from keyboard dispatcher tracking
		this.keyboardDispatcher.remove(this);
	}

	private _attach() {
		// todo: do it like angular so we have this method public
		// and we transfer the portalElement over here?
		// it should work with referencing the portalElement in e.g. select.ts
		// to calculate stuff. Because it works in menu.ts too (this.menu.portalElement.parentElement is c-overlay)!
		// Maybe then we can use ._overlayRef.position()... instead of OverlayPositionBuilder

		if (this.options.positionStrategy) {
			this.options.positionStrategy.attach(this);
		}

		if (this.options.scrollStrategy) {
			this.options.scrollStrategy.enable();
		}

		this.overlayElement.appendChild(this.portalElement);

		this._updateElementSize();

		this.overlayElement.style.pointerEvents = 'auto';

		if (this.options.hasBackdrop) {
			this._attachBackdrop();
		}

		this.attached = true;

		// Track this overlay by the keyboard dispatcher
		this.keyboardDispatcher.add(this);

		if (this.onAttached) {
			this.onAttached();
		}
	}

	private _detach() {
		if (!this.hasAttached()) {
			return;
		}

		this.detachBackdrop();

		// When the overlay is detached, the element should disable pointer events.
		// This is necessary because otherwise the element will cover the page and disable
		// pointer events therefore. Depends on the position strategy and the applied boundaries.
		this.overlayElement.style.pointerEvents = 'none';

		if (this.options.positionStrategy && this.options.positionStrategy.detach) {
			this.options.positionStrategy.detach();
		}

		if (this.options.scrollStrategy) {
			this.options.scrollStrategy.disable();
		}

		// Remove this overlay from keyboard dispatcher tracking
		this.keyboardDispatcher.remove(this);

		if (this.onDetached) {
			this.onDetached();
		}
	}

	/** Attaches a backdrop for this overlay. */
	private _attachBackdrop() {
		this.backdropElement = createNode(
			`<div class="${this.options.className}__backdrop"></div>`
		);

		if (this.options.backdropClassName) {
			addClass(this.backdropElement, this.options.backdropClassName);
		}

		// Insert the backdrop before the overlay in the DOM order,
		// in order to handle stacked overlays properly.
		this.overlayElement.parentElement!.insertBefore(
			this.backdropElement,
			this.overlayElement
		);

		// Forward backdrop clicks such that the consumer of the overlay can perform whatever
		// action desired when such a click occurs (usually closing the overlay).
		this.backdropElement.addEventListener('click', (event: MouseEvent) =>
			this._handleBackdropClick(event)
		);

		// Add class to fade-in the backdrop after one frame.
		requestAnimationFrame(() => {
			if (this.backdropElement) {
				addClass(this.backdropElement, 'is-showing');
			}
		});
	}

	/** Updates the size of the overlay element based on the overlay config. */
	private _updateElementSize() {
		if (this.options.width || this.options.width === 0) {
			this.overlayElement.style.width = formatCssUnit(this.options.width);
		}

		if (this.options.height || this.options.height === 0) {
			this.overlayElement.style.height = formatCssUnit(this.options.height);
		}

		if (this.options.minWidth || this.options.minWidth === 0) {
			this.overlayElement.style.minWidth = formatCssUnit(this.options.minWidth);
		}

		if (this.options.minHeight || this.options.minHeight === 0) {
			this.overlayElement.style.minHeight = formatCssUnit(
				this.options.minHeight
			);
		}

		if (this.options.maxWidth || this.options.maxWidth === 0) {
			this.overlayElement.style.maxWidth = formatCssUnit(this.options.maxWidth);
		}

		if (this.options.maxHeight || this.options.maxHeight === 0) {
			this.overlayElement.style.maxHeight = formatCssUnit(
				this.options.maxHeight
			);
		}
	}

	private _handleBackdropClick(event: MouseEvent) {
		if (
			!isAncestorOfClass(event.target as Element, this.options.className, true)
		) {
			if (this.backdropClick) {
				this.backdropClick(event);
			}
		}
	}
}

export function overlay(
	element: HTMLElement,
	options?: OverlayOptions
): Overlay {
	const overlayRef = new Overlay(element, options);

	overlayRef.create();

	return overlayRef;
}

function formatCssUnit(value: number | string) {
	// todo: @opten/typescript?
	return typeof value === 'string' ? (value as string) : `${value}px`;
}

/** Size properties for an overlay. */
export interface OverlaySizeConfig {
	width?: number | string;
	height?: number | string;
	minWidth?: number | string;
	minHeight?: number | string;
	maxWidth?: number | string;
	maxHeight?: number | string;
}
